import React, { useState } from 'react';

import { bindActionCreators } from '@reduxjs/toolkit';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import type { SubscriptionAdvertisement } from '../../@types/AdvertismentSubscription';
import type { ElasticVehicle } from '../../@types/ElasticVehicle';
import type { ParsedBranding } from '../../@types/branding';
import type Item from '../../@types/item';
import {
  trackLoginModuleClickLoginSaveObjectInSearchResult as _trackLoginModuleClickLoginSaveObjectInSearchResult,
  trackLoginModuleInViewportSaveObjectInSearchResult as _trackLoginModuleInViewportSaveObjectInSearchResult,
  trackClickItemEvent,
  trackExposureEvent,
} from '../../actions/tracking';
import { urls } from '../../helpers';
import { forItem, forItemWebp } from '../../helpers/images';
import { createItemUrl } from '../../helpers/urls';
import { useDelayedRender } from '../../hooks/renderers';
import type { RootState } from '../../reducers';
import { getFirstItemManufacturerPackaging } from '../../selectors/branding';
import ProductCardComponent, {
  type ProductCardImageProps,
} from '../../styled-components/components/ProductCard';
import ProductCardRemoved from '../../styled-components/components/ProductCard/ProductCardRemoved';
import {
  CONTAINER_MAX_WIDTH,
  MEDIA_QUERY_MIN_MD,
  MEDIA_QUERY_MIN_SM,
} from '../../styled-components/layout/constants';
import SignInModal from '../../styled-components/structures/Modals/SignInModal';
import { getConvertedMileage, getMarketOdometer } from '../../utils/market';
import { getAdvertisementSubscription } from '../../utils/reselectors';
import AdSubscriptionCreationModal from '../subscriptions/ad-subscription-create-modal';
import AdSubscriptionRemovalModal from '../subscriptions/ad-subscription-remove-modal';

export type PrimaryPriceOptions = 'price' | 'privateLeasing' | 'businessLeasing';

interface ProductCardProps {
  item: ElasticVehicle | Item;
  removed?: boolean;
  forwardedRef?: React.MutableRefObject<HTMLDivElement | null>;
  visibleKey?: number;
  primaryPrice?: PrimaryPriceOptions;
  testId?: string;
  onExposure?: () => void;
  onClick?: () => void;
}

const ProductCard = ({
  item,
  removed,
  forwardedRef,
  visibleKey,
  primaryPrice = 'price',
  testId,
  onExposure,
  onClick,
}: ProductCardProps) => {
  const { t } = useTranslation(['common', 'currency', 'glossary', 'search', 'validation']);
  const [reference, setReference] = useState<HTMLDivElement | null>(null);
  const dispatch = useDispatch();

  const actions = bindActionCreators(
    {
      trackExposureEvent,
      trackClickItemEvent,
      trackLoginModuleInViewportSaveObjectInSearchResult:
        _trackLoginModuleInViewportSaveObjectInSearchResult,
      trackLoginModuleClickLoginSaveObjectInSearchResult:
        _trackLoginModuleClickLoginSaveObjectInSearchResult,
    },
    dispatch,
  );

  let forwardedElement = undefined;
  if (forwardedRef && forwardedRef.current) forwardedElement = forwardedRef.current;

  useDelayedRender(
    () => {
      if (onExposure) onExposure();
    },
    reference,
    { forwardedElement, visibleKey },
  );

  // USP List
  const odometer = item.odometerReading
    ? getMarketOdometer(item.odometerReading)
    : getConvertedMileage(item.mileage);

  const uspList = [item.modelYear.toString(), odometer];

  if (item.gearboxType) {
    uspList.push(item.gearboxType);
  }

  if (item.fuelTypes) {
    uspList.push(item.fuelTypes);
  }

  // Image
  let images: ProductCardImageProps = {
    src: urls.cdn('/images/placeholders/placeholder_product_card_small.jpg'),
  };

  const imageDefault = forItem(item, '380x253');
  const imageLarge = forItem(item, '770x514');
  const imageDefaultWebp = forItemWebp(item, '380x253');
  const imageLargeWebp = forItemWebp(item, '770x514');

  if (imageDefault) {
    images = {
      src: imageDefault,
      useBackdrop: false,
      srcSet: imageLarge ? [{ url: imageLarge, width: 770 }] : undefined,
      webp:
        imageDefaultWebp && imageLargeWebp
          ? [
              {
                url: imageDefaultWebp,
                width: 380,
              },
              {
                url: imageLargeWebp,
                width: 770,
              },
            ]
          : undefined,
      sizes: `(min-width: ${CONTAINER_MAX_WIDTH}) 416px,
        (min-width: ${MEDIA_QUERY_MIN_MD}) calc(33.333vw - (128px / 3)),
        (min-width: ${MEDIA_QUERY_MIN_SM}) calc(50vw - (48px / 2)),
        calc(100vw - 32px)`,
    };
  }

  // Price
  const price = [];

  if (item.price) {
    price.push({
      id: 'price',
      label:
        item.inventoryStatusString === 'Order'
          ? t('priceLabel.recommendedRetailPrice', { ns: 'common' })
          : t('priceLabel.cash', { ns: 'common' }),
      value: item.price,
      currency: t('currencySymbol', { ns: 'currency' }),
    });
  }

  if (item.leasingPrice) {
    price.push({
      id: 'privateLeasing',
      label: t('priceLabel.privateLeasing', { ns: 'common' }),
      value: item.leasingPrice,
      currency: t('monthlyCostSymbol', { ns: 'currency' }),
    });
  }

  if (item.businessLeasingPrice) {
    price.push({
      id: 'businessLeasing',
      label: t('priceLabel.businessLeasing', { ns: 'common' }),
      value: item.businessLeasingPrice,
      currency: t('monthlyCostSymbol', { ns: 'currency' }),
    });
  }

  // Place primary price first in price array
  const primaryPriceIndex = price.findIndex((p) => p.id === primaryPrice);
  if (primaryPriceIndex > -1) {
    const [primaryPriceItem] = price.splice(primaryPriceIndex, 1);
    price.unshift(primaryPriceItem);
  }

  // Guarantee Package Logo
  const packaging = useSelector<RootState, ParsedBranding | null | undefined>((s) =>
    getFirstItemManufacturerPackaging(s, item),
  );

  const guaranteePackageLogo = packaging
    ? {
        src: `${packaging.logo}?w=128&h=32`,
        srcSet: `${packaging.logo}?w=256&h=64 2x`,
        alt: packaging.name,
      }
    : undefined;

  // Save
  const authenticated = useSelector<RootState, string | null>((s) => s.auth.token);
  const subscription = useSelector<RootState, SubscriptionAdvertisement | null | undefined>((s) =>
    getAdvertisementSubscription(s, item),
  );

  const [requestSignIn, setRequestSignIn] = useState(false);
  const [displaySubscribeModal, setDisplaySubscribeModal] = useState(false);
  const [displayRemovalModal, setDisplayRemovalModal] = useState(false);

  const onSave = () => {
    if (!authenticated) {
      setRequestSignIn(true);
    } else {
      if (subscription) {
        setDisplayRemovalModal(true);
      } else {
        setDisplaySubscribeModal(true);
      }
    }
  };

  const title = item.shortDescription ? `${item.title} ${item.shortDescription}` : item.title;

  return (
    <>
      {removed ? (
        <ProductCardRemoved
          title={title}
          image={images}
          location={item.position?.city || t('unknown', { ns: 'validation' })}
          sellerName={item.branches ? item.branches[0].name : t('unknown', { ns: 'validation' })}
          uspList={uspList}
          save={{
            isSaved: !!subscription,
            onClick: onSave,
          }}
          testId={testId}
        />
      ) : (
        <ProductCardComponent
          adStatus={item.status}
          to={createItemUrl(item)}
          title={title}
          image={images}
          inventoryStatus={item.inventoryStatusString}
          location={item.position?.city || t('unknown', { ns: 'validation' })}
          sellerName={item.branches ? item.branches[0].name : t('unknown', { ns: 'validation' })}
          uspList={uspList}
          price={price}
          guaranteePackageLogo={guaranteePackageLogo}
          onClick={onClick}
          save={{
            isSaved: !!subscription,
            onClick: onSave,
          }}
          buyOnline={item.commerce && item.commerce.enabled && !item.commerce.disabledOnWayke}
          forwardedRef={setReference}
          testId={testId}
        />
      )}
      {requestSignIn && (
        <SignInModal
          image="/images/modal/save_assignment.png"
          onClose={() => setRequestSignIn(false)}
          title={t('To save the car as a favourite, you must log in', { ns: 'search' })}
          to={`/auth/signin?redirectUrl=${location.pathname}${
            location.search ? location.search : ''
          }`}
          trackOnInit={actions.trackLoginModuleInViewportSaveObjectInSearchResult}
          trackOnSignInClick={actions.trackLoginModuleClickLoginSaveObjectInSearchResult}
        />
      )}
      {displaySubscribeModal && (
        <AdSubscriptionCreationModal
          searchItem={item}
          onClose={() => setDisplaySubscribeModal(false)}
        />
      )}
      {displayRemovalModal && (
        <AdSubscriptionRemovalModal
          onClose={() => setDisplayRemovalModal(false)}
          subscription={subscription}
        />
      )}
    </>
  );
};

export default ProductCard;
